import { Box, Button, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";

const formatMoney = (value: number) =>
  value.toLocaleString("en-US", { style: "currency", currency: "USD" });

interface CartFabProps {
  count: number;
  total: number;
  disabled?: boolean;
  onOpen: () => void;
  onClear: () => void;
}

// Floating shortcut to the payment review. The count is plain text rather than
// a badge so two- and three-digit selections cannot spill out of a bubble.
const CartFab = ({ count, total, disabled, onOpen, onClear }: CartFabProps) => {
  if (count === 0) return null;

  return (
    <Box
      sx={{
        position: "fixed",
        right: { xs: 16, md: 28 },
        bottom: { xs: 16, md: 24 },
        zIndex: 1200,
        display: "flex",
        alignItems: "center",
        gap: 1,
        maxWidth: "calc(100vw - 32px)",
      }}
    >
      <Button
        aria-label="Clear selected invoices"
        onClick={onClear}
        disabled={disabled}
        sx={{
          flex: "0 0 auto",
          minWidth: 38,
          width: 38,
          height: 38,
          p: 0,
          borderRadius: "50%",
          backgroundColor: "#ffffff",
          color: "var(--on-surface, #131517)",
          border: "1px solid var(--tpc-300, #c9c9c9)",
          boxShadow: "0 6px 18px rgba(0, 0, 0, 0.14)",
          "&:hover": {
            backgroundColor: "var(--surface-container-low, #f7f7f7)",
          },
        }}
      >
        <CloseIcon sx={{ fontSize: 18 }} />
      </Button>
      <Button
        onClick={onOpen}
        disabled={disabled}
        aria-label={`Pay ${count} selected ${
          count === 1 ? "invoice" : "invoices"
        }, total ${formatMoney(total)}`}
        sx={{
          minWidth: 0,
          borderRadius: 999,
          pl: 1.75,
          pr: 1.5,
          py: 0.75,
          minHeight: 54,
          backgroundColor: "#ffffff",
          color: "var(--on-surface, #131517)",
          border: "1px solid var(--tpc-300, #c9c9c9)",
          boxShadow: "0 12px 28px rgba(0, 0, 0, 0.18)",
          textTransform: "none",
          "&:hover": {
            backgroundColor: "#ffffff",
            color: "var(--on-surface, #131517)",
            borderColor: "var(--tpc-600, #555759)",
            boxShadow: "0 14px 32px rgba(0, 0, 0, 0.24)",
          },
        }}
      >
        <DescriptionOutlinedIcon sx={{ fontSize: 22, flex: "0 0 auto" }} />
        <Box sx={{ ml: 1.25, mr: 1, minWidth: 0, textAlign: "left" }}>
          <Typography
            noWrap
            sx={{
              fontFamily: '"Brandon Grotesque", sans-serif',
              fontSize: 8,
              fontWeight: 700,
              letterSpacing: "0.14em",
              textTransform: "uppercase",
              lineHeight: 1.2,
              opacity: 0.75,
            }}
          >
            {count} {count === 1 ? "invoice" : "invoices"} selected
          </Typography>
          <Typography
            noWrap
            sx={{
              fontFamily: '"Brandon Grotesque", sans-serif',
              fontSize: 15,
              fontWeight: 700,
              lineHeight: 1.25,
            }}
          >
            {formatMoney(total)}
          </Typography>
        </Box>
        <ArrowForwardIcon sx={{ fontSize: 18, flex: "0 0 auto", opacity: 0.9 }} />
      </Button>
    </Box>
  );
};

export default CartFab;
